export const questionGenerationPrompts = {
  "true-false": `You are creating educational content for a True/False interactive game. Based on the topic provided, generate exactly 5 higher-order thinking questions that encourage critical analysis and application of concepts.

IMPORTANT: Return ONLY a valid JSON object with the following structure:

{
  "title": "Brief, engaging title (4-6 words max)",
  "questions": [
    {
      "question": "A brief, focused question statement as a complete sentence with proper punctuation",
      "correctAnswer": true/false,
      "explanation": "Explanation in 1 sentence with proper punctuation"
    }
  ]
}

Requirements:
- Generate exactly 5 questions
- Focus on analysis, evaluation, and application rather than simple recall
- Each question should be a clear statement that can be definitively true or false
- Explanations should be educational and help reinforce learning
- Use proper punctuation, grammar, and sentence structure
- Avoid obvious or trivial questions
- Make questions thought-provoking and engaging
- Use proper mathematical/scientific notation when needed (e.g., CO₂, m/s²)

Topic: `,

  "drag-and-drop": `You are creating educational content for a Drag-and-Drop matching game. Based on the topic provided, generate exactly 5 items that need to be matched with their corresponding categories or answers.

IMPORTANT: Return ONLY a valid JSON object with the following structure:

{
  "title": "Brief, engaging title (4-6 words max)",
  "items": [
    {
      "id": "Unique identifier for the item example: 1, 2, 3, 4, 5 in order",
      "item": "Draggable item",
      "correctZone": "Correct drop zone text/ short definitions for the item",
      "explanation": "Brief explanation of why this match is correct"
    }
  ],
}

Requirements:
- Generate exactly 5 draggable items
- Items should be challenging but logically matchable
- Focus on conceptual understanding rather than memorization
- Explanations should reinforce learning
- Use proper terminology and notation
- Make matching require thoughtful consideration

Topic: `,

  "sort-and-order": `You are creating educational content for a Sort-and-Order interactive game. Based on the topic provided, generate exactly 5 items that need to be arranged in a specific logical sequence.

IMPORTANT: Return ONLY a valid JSON object with the following structure:

{
  "title": "Brief, engaging title (4-6 words max)",
  "items": [
    {
      "id": "Unique identifier for the item example: 1, 2, 3, 4, 5 in order",
      "item": "Item description",
      "correctOrder": "Correct order/position of the item",
      "explanation": "Brief explanation of why this comes at this position"
    }
  ],
}

Requirements:
- Generate exactly 5 items to be ordered
- Each item should have a clear position in the sequence (1-5)
- The ordering criterion should be educational and meaningful
- Items should require thoughtful analysis to order correctly
- Focus on processes, timelines, hierarchies, or logical sequences
- Explanations should help learners understand the reasoning
- Use proper terminology and notation
- Make the sequence intellectually challenging but learnable

Topic: `,

  "fill-in-the-blanks": `You are creating educational content for a Fill-in-the-Blanks interactive game. Based on the topic provided, generate exactly 5 questions with dropdown options for each blank.

IMPORTANT: Return ONLY a valid JSON object with the following structure:

{
  "title": "Brief, engaging title (4-6 words max)",
  "questions": [
    {
      "id": "Unique identifier for the question example: 1, 2, 3, 4, 5 in order",
      "question": "The question with _____ representing the blank",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correctAnswer": "Correct answer among the 4 options",
      "explanation": "Brief explanation in 1 sentence"
    }
  ]
}

Requirements:
- Generate exactly 5 fill-in-the-blank questions
- Each question should have exactly one blank (use _____)
- Provide 4 options per question (1 correct answer, 3 distractors)
- Focus on key concepts, terminology, or relationships
- Distractors should be plausible but incorrect
- Questions should test understanding, not just memorization
- Explanations should reinforce the concept being tested
- Use proper punctuation and grammar
- Make blanks meaningful to the learning objective

Topic: `,

  "scenario-based": `You are creating educational content for a Scenario-Based Questions interactive game. Based on the topic provided, generate exactly 5 real-world scenarios with multiple-choice responses.

IMPORTANT: Return ONLY a valid JSON object with the following structure:

{
  "title": "Brief, engaging title (4-6 words max)",
  "questions": [
    {
      "scenario": "A brief, focused real-world situation that sets up the context",
      "question": "A clear, direct question about the scenario",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correctIndex": "The index of the correct option (0-3)",
      "explanation": "Clear explanation of why the correct answer is best and why others are less effective (10-15 words)"
    }
  ]
}

Requirements:
- Generate exactly 5 scenario-based questions
- Each scenario should be realistic and relatable
- Focus on decision-making, problem-solving, and application
- Options should represent different approaches or solutions
- Options should be lowercase as phrases
- Scenarios should require analysis and judgment
- Explanations should discuss consequences and reasoning
- Make scenarios engaging and thought-provoking
- Test practical application of concepts
- Use professional, appropriate language
- Scenarios should have clear best practices or solutions

Topic: `,

"multiple-choice": `You are creating educational content for a Multiple Choice interactive game. Based on the topic provided, generate exactly 5 higher-order thinking questions that test application, analysis, and critical thinking skills.

IMPORTANT: Return ONLY a valid JSON object with the following structure:

{
  "title": "Brief, engaging title (4-6 words max)",
  "questions": [
    {
      "question": "The question as a complete sentence with proper punctuation",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correctIndex": "The index of the correct option (0-3)",
      "explanation": "Explanation in 1 sentence with proper punctuation"
    }
  ]
}

Requirements:
- Generate exactly 5 questions
- Each question should have exactly 4 options
- Focus on higher-order thinking: application, analysis, evaluation
- Avoid simple recall questions
- Create plausible distractors that are partially correct but not the best answer
- All options should be parallel in structure and similar in length
- Options should be complete sentences with proper punctuation
- Explanations should educate and reinforce learning concepts
- Use proper mathematical/scientific notation when needed (e.g., CO₂, m/s²)
- Ensure questions test deep understanding rather than memorization

Topic: `,

  "flashcards": `You are creating educational content for a Flashcards interactive game. Based on the topic provided, generate exactly 5 questions and answers.
  
  IMPORTANT: Return ONLY a valid JSON object with the following structure:

  {
    "title": "Brief, engaging title (4-6 words max)",
    "questions": [
      {
        "question": "A brief, focused question statement as a complete sentence with proper punctuation",
        "answer": "The answer as a complete sentence with proper punctuation"
      }
    ]
  }

  Requirements:
  - Generate exactly 5 questions and answers
  - Each question should be a clear statement
  - Explanations should be educational and help reinforce learning
  - Use proper punctuation, grammar, and sentence structure
  - Avoid obvious or trivial questions
  - Make questions thought-provoking and engaging
  
  Topic: `
};

export const questionRefinementPrompt = `You are refining educational content for an interactive game. Based on the existing questions and the user's refinement instruction, generate an updated set of questions.

IMPORTANT: 
- Keep the same JSON structure as the original
- Maintain the same topic/subject area unless specifically asked to change it
- If the instruction is about general improvement, enhance question clarity, educational value, and engagement while keeping the same topic and difficulty
- Only modify what the user specifically requests, or improve generally if asked
- Don't duplicate existing questions unless asked
- Keep the same number of questions unless specifically requested to add/remove
- Ensure all questions are educationally sound and appropriately challenging
- Return ONLY a valid JSON object with the same structure as the original

Original Questions Data:
{originalData}

User Refinement Instruction: {userInstruction}

Generate the refined questions following the same structure and format as the original data:`;

export const getQuestionPrompt = (gameType) => {
  return questionGenerationPrompts[gameType] || null;
}; 